import { useCallback, useState } from 'react';
import { Company } from 'types/company.types';

/**
 * Manages all tenant-related dialog states in account management
 */

interface CreateAndEditTenantDialogState {
  open: boolean;
  loading: boolean;
  tenant: Company | null;
  name: string;
  addressLine1: string;
  postalCode: string;
  city: string;
}

interface DeleteTenantDialogState {
  open: boolean;
  loading: boolean;
  tenant: Company | null;
}

interface AgencyBindingDialogState {
  open: boolean;
  loading: boolean;
  tenant: Company | null;
  selectedAgencyTenantId: string | null;
  agencyOptions: { id: string; tenantName: string; userName: string; email: string }[];
}

interface AdminLoginDialogState {
  open: boolean;
  loading: boolean;
  userEmail: string;
  tenantId: string;
  adminEmail: string;
  password: string;
  helperTextUsername: string;
  helperTextPassword: string;
}

const initialCreateAndEditTenantDialogState: CreateAndEditTenantDialogState = {
  open: false,
  loading: false,
  tenant: null,
  name: '',
  addressLine1: '',
  postalCode: '',
  city: '',
};

const initialDeleteTenantDialogState: DeleteTenantDialogState = {
  open: false,
  loading: false,
  tenant: null,
};

const initialAgencyBindingDialogState: AgencyBindingDialogState = {
  open: false,
  loading: false,
  tenant: null,
  selectedAgencyTenantId: null,
  agencyOptions: [],
};

const initialAdminLoginDialogState: AdminLoginDialogState = {
  open: false,
  loading: false,
  userEmail: '',
  tenantId: '',
  adminEmail: '',
  password: '',
  helperTextUsername: '',
  helperTextPassword: '',
};

export function useTenantDialogs() {
  const [createAndEditTenantDialog, setCreateAndEditTenantDialog] = useState<CreateAndEditTenantDialogState>(initialCreateAndEditTenantDialogState);
  const [deleteTenantDialog, setDeleteTenantDialog] = useState<DeleteTenantDialogState>(initialDeleteTenantDialogState);
  const [agencyBindingDialog, setAgencyBindingDialog] = useState<AgencyBindingDialogState>(initialAgencyBindingDialogState);
  const [adminLoginDialog, setAdminLoginDialog] = useState<AdminLoginDialogState>(initialAdminLoginDialogState);

  // Reset handlers
  const resetCreateAndEditTenantDialog = useCallback(() => {
    setCreateAndEditTenantDialog(initialCreateAndEditTenantDialogState);
  }, []);

  const resetDeleteTenantDialog = useCallback(() => {
    setDeleteTenantDialog(initialDeleteTenantDialogState);
  }, []);

  const resetAgencyBindingDialog = useCallback(() => {
    setAgencyBindingDialog(initialAgencyBindingDialogState);
  }, []);

  const resetAdminLoginDialog = useCallback(() => {
    setAdminLoginDialog(initialAdminLoginDialogState);
  }, []);

  return {
    // States and setters
    createAndEditTenantDialog,
    setCreateAndEditTenantDialog,
    resetCreateAndEditTenantDialog,

    deleteTenantDialog,
    setDeleteTenantDialog,
    resetDeleteTenantDialog,

    agencyBindingDialog,
    setAgencyBindingDialog,
    resetAgencyBindingDialog,

    adminLoginDialog,
    setAdminLoginDialog,
    resetAdminLoginDialog,
  };
}
